import styled from 'styled-components';
import { Formik, useField } from 'formik';
import { useCallback, useMemo } from 'react';
import { ActionMeta, GroupBase, Options } from 'react-select';
import {
  default as Select,
  Props as SelectProps,
} from '@paradigm/design-system/src/components/Select';
import {
  FiltersState,
  usePositionFiltersView,
} from '@paradigm/features/src/positions/contexts/filters/store';
import { FormOption } from '@paradigm/features/src/unified-rfqs/ui/rfq-builder/types';
import {
  Body,
  Footer,
} from '#/components/UnifiedRfqs/rfq-filters/RfqFilterModal';
import Button from '@paradigm/design-system/src/components/Button';

interface MultiSelectProps
  extends Omit<
    SelectProps<FormOption, true, GroupBase<FormOption>>,
    'name' | 'onChange' | 'options'
  > {
  readonly name: string;
  readonly options: ReadonlyArray<FormOption>;
}

interface Props {
  readonly onSubmit: (values: FiltersState) => void;
  readonly onReset: () => void;
}

export default function PositionFiltersForm({ onSubmit, onReset }: Props) {
  const {
    activeFiltersCount: _,
    venueOptions,
    assetOptions,
    productOptions,
    instrumentOptions,
    accountOptions,
    ...state
  } = usePositionFiltersView();

  return (
    <Formik initialValues={state} onSubmit={onSubmit} onReset={onReset}>
      {({
        handleSubmit,
        handleReset,
        values: { accounts, assets, instruments, products, venues },
      }) => (
        <form onSubmit={handleSubmit}>
          <Body>
            <Title>Venue</Title>
            <MultiSelect
              name="venues"
              value={venues.map((v) => ({ value: v, label: v }))}
              options={venueOptions}
            />
            <Title>Asset</Title>
            <MultiSelect
              name="assets"
              value={assets.map((a) => ({ value: a, label: a }))}
              options={assetOptions}
            />
            <Title>Product</Title>
            <MultiSelect
              name="products"
              value={products.map((p) => ({ value: p, label: p }))}
              options={productOptions}
            />
            <Title>Instrument</Title>
            <MultiSelect
              name="instruments"
              value={instruments.map((p) => ({ value: p, label: p }))}
              options={instrumentOptions}
            />
            <Title>Account</Title>
            <MultiSelect
              name="accounts"
              value={accounts.map((p) => ({ value: p, label: p }))}
              options={accountOptions}
            />
          </Body>
          <Footer>
            <Button
              variant="link"
              size="large"
              ariaLabel="clear all filters"
              onClick={handleReset}
            >
              Clear All
            </Button>
            <Button size="large" type="submit" ariaLabel="submit filter">
              Filter
            </Button>
          </Footer>
        </form>
      )}
    </Formik>
  );
}

function MultiSelect(props: MultiSelectProps) {
  const { name, options = [] } = props;
  const [field] = useField<string[]>({ name, multiple: true });

  const selectedOptions = useMemo(
    () =>
      options.filter(
        (option) => option.value != null && field.value.includes(option.value),
      ),
    [field.value, options],
  );

  const onChange = useCallback(
    (value: Options<FormOption>, _action: ActionMeta<FormOption>) => {
      field.onChange({
        target: {
          name: field.name,
          value: (value as ReadonlyArray<FormOption>).map(
            (option) => option.value,
          ),
        },
      });
    },
    [field],
  );

  return (
    <Select
      isMulti
      {...props}
      placeholder="Search..."
      value={selectedOptions}
      onChange={onChange}
    />
  );
}

const Title = styled.div`
  margin-bottom: 4px;
  margin-top: 12px;
  font-size: 13px;
  line-height: 16px;
  font-weight: bold;
`;
